<template>
  <div>
    <q-input
      ref="input"
      v-model="vin"
      :class="{
        'has-hint': vehicle.key && !vehicle.isVinEditable,
        'q-mb-xs': vinError,
      }"
      :error="vinError"
      :error-message="submissionError ? vinError : undefined"
      filled
      hide-bottom-space
      :hint="vehicle.key && !vehicle.isVinEditable ? 'Reported by vehicle and cannot be edited' : undefined"
      label="VIN"
      lazy-rules
      :readonly="isReadonly"
      :rules="[validateInput]"
    >
      <template #after>
        <ScannerButton v-if="!vehicle.key || vehicle.isVinEditable" validation="vin" @scan="onScan" />
      </template>
    </q-input>

    <InvalidCharsBanner
      v-if="(wasNormalized && isValid) || correctionApplied"
      bg-color="blue-1"
      button-text="Use Corrected VIN"
      :character-errors="characterCorrections"
      :confirmed="correctionApplied"
      confirmed-text="The corrected VIN has been applied."
      diff-highlight-color="positive"
      diff-highlight-color-label="green"
      diff-type="Suggested changes"
      icon="info"
      icon-color="blue-4"
      show-character-diff
      @confirm="useSuggested"
    />

    <InvalidCharsBanner
      v-if="!isValid || warningsIgnored"
      bg-color="amber-2"
      button-text="Use VIN Anyway"
      :character-errors="characterErrors"
      :confirmed="warningsIgnored"
      confirmed-text="VIN confirmed as entered."
      diff-highlight-color="negative"
      diff-highlight-color-label="red"
      diff-type="Invalid characters"
      icon="warning"
      icon-color="brown-10"
      :must-be-valid="mustBeValid"
      :show-character-diff="Boolean(attemptedNormalization && invalidCharGroups.length)"
      @confirm="ignoreWarnings"
    >
      <ul class="q-mt-none q-mb-none">
        <li v-for="(error, index) in nonCharacterErrors" :key="index" class="q-mt-sm" dense>
          {{ error.message }}
        </li>
        <li v-if="invalidCharGroups.length" class="q-mt-sm">This VIN contains characters that aren’t normally used.</li>
      </ul>
    </InvalidCharsBanner>
  </div>
</template>

<script setup lang="ts">
import type { QInput } from 'quasar';
import { computed, onMounted, ref, toRaw, useTemplateRef, watch } from 'vue';
import { useStore } from 'vuex';
import ScannerButton from 'components/scanner/ScannerButton.vue';
import type Vehicle from 'src/models/Vehicle';
import { minorNormalization, validateVin } from 'src/services/vin';
import type { VinError } from 'src/types/vin';
import InvalidCharsBanner from './InvalidCharsBanner.vue';

const VIN_IS_REQUIRED = 'A VIN is required';

const props = withDefaults(
  defineProps<{
    existingVinErrorMessage?: string;
    mustBeValid?: boolean;
    originalDriverKey?: string | null;
    submissionError?: boolean;
    vehicle: Vehicle;
  }>(),
  {
    existingVinErrorMessage: "Please double check that you've entered the correct VIN.",
  }
);

const emit = defineEmits(['update:model-value']);

defineExpose({ focus, validate });

const store = useStore();

const existingVins = computed(
  () => new Set(store.getters['assets/vehicles'].filter(({ key }) => key !== props.vehicle.key).map(({ vin }) => vin))
);

const vin = ref('');
const input = useTemplateRef<QInput>('input');

const allowEditVehicle = computed(() => store.getters['session/allowEditVehicle']);
const currentUser = computed(() => store.getters['session/currentUser']);

const vinError = computed(() => {
  const submissionError = toRaw(props.submissionError);
  let [error] = Array.isArray(submissionError) ? submissionError : [submissionError];

  if (error && error.includes('Missing data')) {
    error = VIN_IS_REQUIRED;
  }

  return error || (Boolean(errors.value.length) && Boolean(props.mustBeValid));
});

const isViewerAndDriver = computed(
  () => currentUser.value.isViewer() && props.originalDriverKey === currentUser.value.key
);

const isReadonly = computed(
  () => (!allowEditVehicle.value && !isViewerAndDriver.value) || (props.vehicle.key && !props.vehicle.isVinEditable)
);

function onScan(_: Event, value: string) {
  vin.value = value;
  input.value.resetValidation();
  validateInput();
}

const errors = ref<VinError[]>([]);
const isValid = ref(true);
const originalValue = ref('');
const lastValueProcessed = ref('');
const rawErrors = ref<VinError[]>([]);
const attemptedNormalization = ref(false);
const wasNormalized = ref(false);
const suggestedVin = ref('');
const correctionApplied = ref(false);
const warningsIgnored = ref(false);
function validateInput() {
  if (isReadonly.value) {
    return true; // Do not valid if field is not editable
  }

  if (forceRevalidation.value) {
    correctionApplied.value = false;
    warningsIgnored.value = false;
    lastValueProcessed.value = '';
    forceRevalidation.value = false;
  }

  if (props.submissionError) {
    return props.submissionError;
  }

  if (!vin.value?.trim()) {
    reset();
    return VIN_IS_REQUIRED;
  }

  if (existingVins.value.has(vin.value) && props.vehicle.vin !== vin.value) {
    const vehicleWithVin = store.getters['assets/vehicles'].find((vehicle) => vehicle.vin === vin.value);
    return `There is already a vehicle with that VIN (${vehicleWithVin.nickname}). ${props.existingVinErrorMessage}`;
  }

  const vinCleaned = minorNormalization(vin.value);

  vin.value = vinCleaned; // Apply cleaned up VIN

  if (vinCleaned === lastValueProcessed.value) {
    // Already processed the given VIN
    return isValid.value || '';
  }

  reset();

  const result = validateVin(vin.value);

  isValid.value = result.valid;
  errors.value = result.errors;
  rawErrors.value = result.rawErrors;
  wasNormalized.value = result.wasNormalized;
  attemptedNormalization.value = result.attemptedNormalization;
  suggestedVin.value = result.vin;

  lastValueProcessed.value = vinCleaned;

  emit('update:model-value', vin.value);

  return isValid.value || '';
}

function reset() {
  errors.value = [];
  rawErrors.value = [];
  attemptedNormalization.value = false;
  wasNormalized.value = false;
  isValid.value = true;
  originalValue.value = vin.value;
  correctionApplied.value = false;
  warningsIgnored.value = false;
}

const CHARACTER_MAP = {
  I: '1',
  O: '0',
  Q: '0',
};
const CHARACTER_MAP_TOOLTIP = {
  I: '"1"',
  O: '"0" (zero)',
  Q: '"0" (zero)',
};

const invalidCharGroups = computed(() =>
  rawErrors.value.filter(({ code }) => ['forbidden_letters', 'invalid_chars'].includes(code))
);

const characterCorrections = computed(() => {
  const characterArray = originalValue.value.split('').map(
    (character) =>
      ({
        value: character,
      }) as { value: string; error: string }
  );

  let forbiddenChars = [];
  invalidCharGroups.value.forEach(({ indices }) => {
    forbiddenChars = [...forbiddenChars, ...indices];
  });

  if (!forbiddenChars) {
    return characterArray;
  }

  forbiddenChars.forEach((index) => {
    const characterData = characterArray[index];
    const character = characterData.value;
    const characterReplacement = CHARACTER_MAP_TOOLTIP[character];

    if (characterReplacement) {
      characterData.error = `"${character}" changed to ${characterReplacement}`;
    } else {
      characterData.error = `"${character}" was removed`;
    }

    characterData.value = CHARACTER_MAP[character];
  });

  return characterArray;
});

const characterErrors = computed(() => {
  const characterArray = originalValue.value.split('').map(
    (character) =>
      ({
        value: character,
      }) as { value: string; error: string }
  );

  if (!invalidCharGroups.value.length) {
    return characterArray;
  }

  invalidCharGroups.value.forEach(({ indices }) => {
    indices.forEach((index) => {
      const characterData = characterArray[index];
      const character = characterData.value;
      const characterAlt = CHARACTER_MAP_TOOLTIP[character];

      characterData.error = `"${character}" is invalid.`;
      if (characterAlt) {
        characterData.error = `${characterData.error} Should this be a ${characterAlt}?`;
      }
    });
  });

  return characterArray;
});

const nonCharacterErrors = computed(() =>
  errors.value.filter(({ code }) => !['forbidden_letters', 'invalid_chars'].includes(code))
);

function useSuggested() {
  reset();
  vin.value = suggestedVin.value;
  lastValueProcessed.value = vin.value;
  correctionApplied.value = true;
  input.value?.validate();
}

function ignoreWarnings() {
  reset();
  warningsIgnored.value = true;
  input.value?.validate();
}

function focus() {
  input.value.focus();
}

const forceRevalidation = ref(false);
function validate(revalidate = false) {
  forceRevalidation.value = revalidate;
  return input.value.validate();
}

onMounted(() => {
  vin.value = props.vehicle.vin;
});

watch(
  () => props.vehicle,
  () => {
    vin.value = props.vehicle.vin;
  }
);

watch(
  () => props.submissionError,
  () => {
    if (props.submissionError) {
      input.value.validate();
    }
  }
);
</script>
